import React from 'react';
import { NativeSyntheticEvent, ScrollView, TextInputKeyPressEventData, View } from 'react-native';
import { translate } from '../../services/translate';
import MyAppText from '../MyAppText';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import FormInput from '../FormInput';
import Badge from '../Badge';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

interface PlateImageProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    required?: boolean;
}

interface PlateFormInput {
    plate: string;
}

export default function RegisterPlateInput({ form, fieldName, required }: PlateImageProps) {
    const { styles, theme } = useStyles(styleSheet);


    const defaultEmptyValuesInput: PlateFormInput = {
        // control variable used only for the frontend
        plate: '',
    };
    const formInput = useForm<PlateFormInput>({
        defaultValues: defaultEmptyValuesInput,
        mode: 'onChange',
    });

    function isPlateValid(plate: string): boolean {
        const platePattern = /^[A-Z]{3}-?(?:[0-9][0-9A-Z][0-9]{2}|49CC)$/;
        return platePattern.test(plate);
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            e.preventDefault?.();
            if (!formInput.getFieldState('plate').invalid) {
                if (!form.getValues(fieldName).includes(formInput.getValues('plate'))) {
                    const newPlates = [...form.getValues(fieldName), formInput.getValues('plate')];
                    form.setValue(fieldName, newPlates, { shouldValidate: true });
                    formInput.setValue('plate', '');
                }
            }
        }
    }

    function removeItem(index: number) {
        const newItems = [...form.getValues(fieldName)];
        newItems.splice(index, 1);
        form.setValue(fieldName, newItems, { shouldValidate: true });
    }

    return (
        <Controller
            control={form.control}
            name={fieldName}
            rules={{
                validate: {
                    hasPlates: (value) => {
                        if (!required) {
                            return true;
                        }
                        if (value && value.length > 0) {
                            return true;
                        }

                        const currentPlate = formInput.watch('plate');
                        return (currentPlate.trim() !== '' && isPlateValid(currentPlate)) || translate('required_field');
                    }
                }
            }}
            render={({ field, fieldState: { error } }) => (
                <View style={{ gap: 8 }}>
                    <FormInput
                        label={translate('vehiclePlate')}
                        form={formInput}
                        fieldName={'plate'}
                        maxLength={7}
                        toUppercase
                        labelStyle={styles.fieldsLabelStyle}
                        pattern={{
                            value: new RegExp(/^[A-Z]{3}-?(?:[0-9][0-9A-Z][0-9]{2}|49CC)$/),
                            message: translate('invalidPlate')
                        }}
                        customValidations={{
                            hasDuplicate: (data) => {
                                if (form.getValues(fieldName).includes(data)) {
                                    return translate('plateAlreadyAdded');
                                }
                                return true;
                            }
                        }}
                        onKeyPress={handleKeyDown}
                    />
                    <ScrollView
                        style={{ maxHeight: 80 }}
                        contentContainerStyle={{
                            flexDirection: 'row',
                            columnGap: 8,
                            rowGap: 4,
                            flexWrap: 'wrap'
                        }}
                    >
                        {field.value?.map((item: string, index: number) => (
                            <Badge
                                mode='ROUNDED'
                                key={index}
                                backgroundColor={theme.colors.backgrounds['BG-4']}
                                textColor={theme.colors.primary['500']}
                                removable
                                onRemove={() => removeItem(index)}
                            >
                                {item}
                            </Badge>
                        ))}
                    </ScrollView>
                    {error && (
                        <MyAppText style={{ color: theme.colors.errors['300'], fontSize: 12 }}>
                            {error.message}
                        </MyAppText>
                    )}
                </View>
            )}
        />
    );
}

const styleSheet = createStyleSheet((theme) => ({
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
}));
